/**
 * Resolve the FULL brand theme to bake into a scaffold.
 *
 * The platform's /api/config is the source of brand truth. The runtime-theming
 * work makes it return a fully-derived `theme`; to avoid a hard cross-dependency
 * on that rollout, this resolver takes the simplest stable path:
 *
 *   1. if /api/config returns a `theme` object → use it verbatim;
 *   2. else derive one with @ghosty/branding from the raw inputs /api/config
 *      already exposes (colors.primary + optional mode/font/logoUrl/secondary);
 *   3. else (no brand inputs at all) fall back to Ghosty's DEFAULT_BRAND.
 *
 * @ghosty/branding is bundled into the CLI (tsup noExternal), so this stays
 * self-contained — no separate npm install at runtime.
 */
import {
  derivePalette,
  fontPairing,
  googleFontsUrl,
  DEFAULT_BRAND,
} from '@ghosty/branding';
import type { ScaffoldTheme } from './scaffold.js';
import type { PlatformConfig, PlatformTheme } from '../client.js';
import type { Branding } from '../profiles.js';

/** Derive a ScaffoldTheme from raw brand inputs via @ghosty/branding. */
function deriveTheme(input: {
  mode?: 'light' | 'dark';
  primary?: string;
  secondary?: string;
  font?: string;
  logoUrl?: string;
}): ScaffoldTheme {
  const tokens = derivePalette({
    mode: input.mode ?? DEFAULT_BRAND.mode,
    primary: input.primary ?? DEFAULT_BRAND.primary,
    // House secondary only alongside the house primary — a brand that set
    // its own primary keeps the derived (rotated) secondary.
    secondary: input.secondary ?? (input.primary ? undefined : DEFAULT_BRAND.secondary),
  });
  const fp = fontPairing(input.font ?? DEFAULT_BRAND.fontKey);
  return {
    tokens,
    fontDisplayStack: fp.display.stack,
    fontMonoStack: fp.mono.stack,
    fontGoogleUrl: googleFontsUrl(fp),
    logoUrl: input.logoUrl,
  };
}

/**
 * Resolve the theme from the live platform config (preferred) falling back to
 * the cached profile branding, then to Ghosty's house brand. Pure: no network.
 */
export function resolveTheme(
  platform: PlatformConfig | undefined,
  profileBranding: Branding | undefined,
): ScaffoldTheme {
  // 1. Stamp already derived the theme → use it, but only trust what's
  //    actually there. Post-rebrand /api/config sends a PARTIAL theme
  //    ({mode, tokens, font} — no font stacks); trusting it verbatim crashed
  //    `ghosty init` on every live stamp. Keep the stamp's tokens and derive
  //    the missing font stacks from its font key.
  if (platform?.theme && platform.theme.tokens) {
    const t = platform.theme as Partial<PlatformTheme> & {
      tokens: PlatformTheme['tokens'];
      font?: string;
    };
    if (typeof t.fontDisplayStack === 'string' && typeof t.fontMonoStack === 'string') {
      return t as unknown as ScaffoldTheme; // genuinely complete
    }
    const fp = fontPairing(t.font ?? platform.font ?? DEFAULT_BRAND.fontKey);
    return {
      tokens: t.tokens as unknown as ScaffoldTheme['tokens'],
      fontDisplayStack: fp.display.stack,
      fontMonoStack: fp.mono.stack,
      fontGoogleUrl: googleFontsUrl(fp),
      logoUrl: t.logoUrl ?? platform.logoUrl ?? profileBranding?.logoUrl ?? undefined,
    };
  }

  // 2. Derive from raw inputs. Live /api/config wins; cached profile fills gaps.
  const primary =
    platform?.colors?.primary ?? profileBranding?.colors?.primary ?? undefined;
  return deriveTheme({
    mode: platform?.mode,
    primary,
    secondary: platform?.colors?.secondary ?? profileBranding?.colors?.secondary,
    font: platform?.font,
    logoUrl: platform?.logoUrl ?? profileBranding?.logoUrl ?? undefined,
  });
}
